// Little lives around town, drawn in game pixels: the town cat (out at night), two puppies (by day), sparks
// off the smith's anvil and sawdust off the carpenter's bench, and the moon's reflection on the water at night.
import ink from '../ink/ink.js';
import { SUB } from '../../../shared/constants.js';
import { T } from '../../../shared/zonebuild.js';
import type { ZoneMap } from '../../../shared/map.js';
import type { Scene } from './scene.js';
import type { Npc } from '../game/npcs.js';

const I = ink as any;
const { INK, TAU } = I;

export interface Critter { kind: 'cat' | 'pup'; x: number; y: number; tx: number; ty: number; dir: number; next: number; col: string; sit: number; hx: number; hy: number; seed: number }
/** Who lives on this map, and when: the cat at night, the pups by day (in the square only). */
export function makeCritters(map: ZoneMap): Critter[] {
  if (map.id !== 'town') return [];
  const at = (kind: Critter['kind'], tx: number, ty: number, col: string, seed: number): Critter => {
    const [x, y] = map.nearestFreeSub(tx, ty);
    return { kind, x: x / SUB, y: y / SUB, tx: x / SUB, ty: y / SUB, dir: 1, next: 0, col, sit: 0, hx: x / SUB, hy: y / SUB, seed };
  };
  return [at('cat', 22, 9, '#5a5560', 1), at('pup', 16, 17, '#e2c08a', 2), at('pup', 18, 18, '#f4efe4', 3)];
}
const outNow = (c: Critter, night: boolean) => c.kind === 'cat' ? night : !night;

/** Wander, sit a while, wander again; the pups chase each other a little and come over to say hello. */
export function stepCritters(list: Critter[], dt: number, now: number, map: ZoneMap, px: number, py: number, night: boolean): void {
  for (const c of list) {
    if (!outNow(c, night)) continue;
    const dx = c.tx - c.x, dy = c.ty - c.y, d = Math.hypot(dx, dy), speed = c.kind === 'pup' ? 46 : 30;
    if (d > 0.6) {
      const step = Math.min(d, speed * dt), nx = c.x + dx / d * step, ny = c.y + dy / d * step;
      if (map.canStandSub(Math.round(nx * SUB), Math.round(ny * SUB))) { c.x = nx; c.y = ny; if (Math.abs(dx) > 0.3) c.dir = dx > 0 ? 1 : -1; } else c.tx = c.x, c.ty = c.y;
      continue;
    }
    if (now < c.next) continue;
    c.next = now + (c.kind === 'cat' ? 4000 + Math.random() * 7000 : 1200 + Math.random() * 2600);
    c.sit = Math.random() < (c.kind === 'cat' ? 0.55 : 0.3) ? 1 : 0;
    if (c.sit) continue;
    const toYou = c.kind === 'pup' && Math.hypot(px - c.x, py - c.y) < 90 && Math.random() < 0.35;
    const a = Math.random() * TAU, r = 20 + Math.random() * (c.kind === 'cat' ? 70 : 50);
    const tx = toYou ? px + (Math.random() - 0.5) * 18 : c.hx + Math.cos(a) * r, ty = toYou ? py + 6 : c.hy + Math.sin(a) * r * 0.7;
    if (map.canStandSub(Math.round(tx * SUB), Math.round(ty * SUB))) { c.tx = tx; c.ty = ty; }
  }
}

/** Items to y-sort with everyone else. */
export function critterItems(list: Critter[], g: CanvasRenderingContext2D, now: number, night: boolean): { y: number; draw: () => void }[] {
  return list.filter(c => outNow(c, night)).map(c => ({ y: c.y, draw: () => drawCritter(g, c, now) }));
}

function drawCritter(g: CanvasRenderingContext2D, c: Critter, now: number): void {
  const t = now / 1000 + c.seed, moving = Math.hypot(c.tx - c.x, c.ty - c.y) > 0.6, step = moving ? Math.sin(t * 16) : 0;
  g.save(); g.translate(Math.round(c.x * 3) / 3, Math.round(c.y * 3) / 3); g.scale(c.dir, 1);
  g.fillStyle = 'rgba(62,47,40,0.2)'; g.beginPath(); g.ellipse(0, 0.5, 5, 1.5, 0, 0, TAU); g.fill();
  g.lineWidth = 0.6; g.strokeStyle = INK; g.lineJoin = 'round'; g.lineCap = 'round';
  const body = (x: number, y: number, rx: number, ry: number) => { g.fillStyle = c.col; g.beginPath(); g.ellipse(x, y, rx, ry, 0, 0, TAU); g.fill(); g.stroke(); };
  if (c.kind === 'cat') {
    const sit = c.sit && !moving;
    // tail
    g.beginPath(); g.moveTo(-3.5, sit ? -1.5 : -3); g.quadraticCurveTo(-7, sit ? -1 : -6 + Math.sin(t * 2) * 1.5, -6, sit ? -4.5 : -8 + Math.sin(t * 2)); g.lineWidth = 1.7; g.strokeStyle = INK; g.stroke(); g.lineWidth = 1; g.strokeStyle = c.col; g.stroke(); g.lineWidth = 0.6; g.strokeStyle = INK;
    if (!sit) for (const lx of [-2.2, 2.2]) { g.beginPath(); g.moveTo(lx, -1.5); g.lineTo(lx + step * (lx > 0 ? 0.8 : -0.8), 0); g.stroke(); }
    body(0, sit ? -2.5 : -2.8, sit ? 2.8 : 4, sit ? 2.8 : 2);
    const hx = sit ? 0.8 : 3.4, hy = sit ? -6.2 : -4.6;
    for (const ex of [-1.3, 1.3]) { g.fillStyle = c.col; g.beginPath(); g.moveTo(hx + ex - 0.9, hy - 1.2); g.lineTo(hx + ex * 1.2, hy - 3.6); g.lineTo(hx + ex + 0.9, hy - 1.2); g.closePath(); g.fill(); g.stroke(); }
    body(hx, hy, 2.3, 2);
    g.fillStyle = '#f7d35e'; for (const ex of [-0.8, 0.8]) { g.beginPath(); g.arc(hx + ex + 0.3, hy - 0.2, 0.45, 0, TAU); g.fill(); }   // eyes that catch the lamplight
  } else {
    const wag = Math.sin(t * 14) * 1.2;
    g.beginPath(); g.moveTo(-3.6, -3.2); g.lineTo(-5.4, -5 + wag); g.lineWidth = 1.5; g.stroke(); g.lineWidth = 0.9; g.strokeStyle = c.col; g.stroke(); g.lineWidth = 0.6; g.strokeStyle = INK;
    for (const lx of [-2.2, 2.2]) { g.beginPath(); g.moveTo(lx, -1.5); g.lineTo(lx + step * (lx > 0 ? 0.8 : -0.8), 0); g.lineWidth = 1.1; g.stroke(); }
    g.lineWidth = 0.6;
    body(0, -3, 3.8, 2.3);
    const hx = 3.6, hy = -5.2 + (moving ? Math.abs(step) * 0.4 : 0);
    body(hx, hy, 2.4, 2.1);
    g.fillStyle = shade(c.col, -0.25); g.beginPath(); g.ellipse(hx - 1.2, hy - 0.2, 0.9, 1.7, 0.3, 0, TAU); g.fill(); g.stroke();   // a floppy ear
    g.fillStyle = INK; g.beginPath(); g.arc(hx + 1, hy - 0.4, 0.4, 0, TAU); g.fill(); g.beginPath(); g.arc(hx + 2.3, hy + 0.4, 0.5, 0, TAU); g.fill();
  }
  g.restore();
}
const shade = (c: string, k: number) => I.shade(c, k);

/** Sparks off the anvil, sawdust off the bench, a puff of flour: drawn over someone at work. */
export function workFx(g: CanvasRenderingContext2D, n: Npc, c: any, now: number): void {
  const t = Math.abs(c?.t ?? now / 1000), side = Math.cos(c?.th ?? 0) >= 0 ? 1 : -1, fx = n.x + side * (n.work === 'hammer' ? 14 : 8), fy = n.y - (n.work === 'hammer' ? 7 : 6);
  if (n.work === 'hammer') {
    const v = (t % 1.1) / 1.1; if (v < 0.6 || v > 0.86) return;
    const k = (v - 0.6) / 0.26;
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i - 2.5) * 0.45 + Math.sin(i * 7.3 + Math.floor(t / 1.1)) * 0.2, r = 2 + k * (5 + (i % 3) * 2.5);
      g.globalAlpha = 1 - k; g.fillStyle = i % 2 ? '#f7d35e' : '#f28b4b';
      g.beginPath(); g.arc(fx + Math.cos(a) * r, fy + Math.sin(a) * r + k * k * 4, 0.7, 0, TAU); g.fill();
    }
    g.globalAlpha = 1;
  } else if (n.work === 'saw') {
    for (let i = 0; i < 4; i++) {
      const ph = (t * 1.6 + i / 4) % 1; g.globalAlpha = (1 - ph) * 0.9; g.fillStyle = '#e2c27a';
      g.beginPath(); g.arc(fx + Math.sin(i * 3.1) * 2.5, fy + 2 + ph * 6, 0.6, 0, TAU); g.fill();
    }
    g.globalAlpha = 1;
  } else if (n.work === 'knead') {
    const ph = (t * 0.9) % 1; if (ph > 0.4) return;
    g.globalAlpha = (0.4 - ph) * 1.6; g.fillStyle = '#fbf7ee'; g.beginPath(); g.arc(fx, fy - ph * 8, 1.2 + ph * 3, 0, TAU); g.fill(); g.globalAlpha = 1;
  }
}

/** The moon on the water at night: a soft round reflection and a glade of shimmering streaks under it. Moves a
 *  little with the camera, the way a reflection does. */
export function drawMoonGlade(g: CanvasRenderingContext2D, scene: Scene | null, t: number, cx: number, cy: number, vw: number, vh: number, night: number): void {
  if (!scene || night < 0.05) return;
  const W = scene.w, H = scene.h, tiles = scene.tiles, TS = 16;
  const water = (x: number, y: number) => { const tx = Math.floor(x / TS), ty = Math.floor(y / TS); return tx >= 0 && ty >= 0 && tx < W && ty < H && tiles[ty * W + tx] === T.WATER; };
  // the water in view: the moon's column falls on it a little right of its middle (and drifts as you walk)
  // (the column with the most open water, leaning right of the middle of the view)
  let best = -1, bestScore = 0;
  const tx0 = Math.max(0, Math.floor(cx / TS)), tx1 = Math.min(W, Math.ceil((cx + vw) / TS));
  for (let tx = tx0; tx < tx1; tx++) {
    let n = 0;
    for (let ty = Math.max(0, Math.floor(cy / TS)); ty < Math.min(H, Math.ceil((cy + vh) / TS)); ty++) if (tiles[ty * W + tx] === T.WATER) n++;
    const score = n * (1 - 0.4 * Math.abs((tx * TS - cx) / vw - 0.6));
    if (n >= 3 && score > bestScore) { bestScore = score; best = tx; }
  }
  if (best < 0) return;
  const mx = best * TS + 8 + Math.sin((cx + vw / 2) / 200) * 4;
  g.save();
  for (let y = Math.max(0, Math.floor(cy / 4) * 4); y < Math.min(H * TS, cy + vh); y += 4) {
    if (!water(mx, y)) continue;
    const depth = ((y - cy) / vh), wob = Math.sin(t * 1.4 + y * 0.31) * 2.5, len = 3 + Math.abs(Math.sin(t * 0.9 + y * 0.17)) * 6 * (0.6 + depth);
    const x0 = mx + wob - len / 2, x1 = mx + wob + len / 2;
    if (!water(x0, y) || !water(x1, y)) continue;
    g.globalAlpha = night * (0.45 + 0.45 * Math.sin(t * 2.2 + y * 0.5) ** 2);
    g.strokeStyle = '#fff6d8'; g.lineWidth = 1.1; g.beginPath(); g.moveTo(x0, y); g.lineTo(x1, y); g.stroke();
  }
  // the disc itself, on the first bit of water under the moon's column
  for (let y = Math.max(0, cy + vh * 0.25); y < Math.min(H * TS, cy + vh); y += 4) {
    if (!water(mx, y) || !water(mx - 6, y) || !water(mx + 6, y) || !water(mx, y + 6)) continue;
    const gr = g.createRadialGradient(mx, y + 2, 0, mx, y + 2, 9);
    gr.addColorStop(0, `rgba(255,248,222,${0.55 * night})`); gr.addColorStop(1, 'rgba(255,248,222,0)');
    g.globalAlpha = 1; g.fillStyle = gr; g.beginPath(); g.ellipse(mx, y + 2, 9, 4.5, 0, 0, TAU); g.fill();
    g.fillStyle = `rgba(255,250,232,${0.7 * night})`; g.beginPath(); g.ellipse(mx + Math.sin(t * 1.1) * 0.6, y + 2, 3.2, 1.6, 0, 0, TAU); g.fill();
    break;
  }
  g.restore();
}
